import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PenLine, Type, Upload, Eraser, X, Check, CalendarDays, CheckSquare, ZoomIn, ZoomOut, Download, Maximize, Minimize, ChevronUp, ChevronDown, Brush, Square } from "lucide-react";
import { deskZ, nextZ } from "@/pages/admin/desktop/deskZ";

/**
 * DigiSigner-style full-screen document editor with two modes:
 *  - mode="sign"    : the signer fills out placeholders + adds their own fields
 *  - mode="prepare" : the sender places placeholder fields for each signer
 *    (signature/initials/text/date/check), color-coded per signer, with
 *    optional prefilled text.
 * Eraser: freehand white-out drawing (like the pen, but white); tap = delete a field.
 */

const SIG_FONTS = [
  { id: "dancing", css: "'Dancing Script', cursive", label: "Dancing Script" },
  { id: "vibes", css: "'Great Vibes', cursive", label: "Great Vibes" },
  { id: "caveat", css: "'Caveat', cursive", label: "Caveat" },
  { id: "pacifico", css: "'Pacifico', cursive", label: "Pacifico" },
  { id: "apple", css: "'Homemade Apple', cursive", label: "Homemade Apple" },
];

const ZOOMS = [0.6, 0.8, 1, 1.25, 1.5, 2];

const SIGNER_PALETTE = [
  { border: "border-emerald-500", bg: "bg-emerald-50/90", text: "text-emerald-700", chip: "bg-emerald-600" },
  { border: "border-sky-500", bg: "bg-sky-50/90", text: "text-sky-700", chip: "bg-sky-600" },
  { border: "border-violet-500", bg: "bg-violet-50/90", text: "text-violet-700", chip: "bg-violet-600" },
  { border: "border-amber-500", bg: "bg-amber-50/90", text: "text-amber-700", chip: "bg-amber-600" },
  { border: "border-rose-500", bg: "bg-rose-50/90", text: "text-rose-700", chip: "bg-rose-600" },
];

function typedSigDataUrl(text, fontCss) {
  const c = document.createElement("canvas");
  c.width = 800; c.height = 220;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0f172a";
  let size = 100;
  ctx.font = `${size}px ${fontCss}`;
  let w = ctx.measureText(text).width;
  if (w > 760) { size = Math.max(24, Math.floor(size * 760 / w)); ctx.font = `${size}px ${fontCss}`; w = ctx.measureText(text).width; }
  ctx.textBaseline = "middle";
  ctx.fillText(text, (c.width - w) / 2, c.height / 2);
  const box = trimBox(ctx, c.width, c.height);
  const t = document.createElement("canvas");
  t.width = box.w; t.height = box.h;
  t.getContext("2d").drawImage(c, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
  return t.toDataURL("image/png");
}

function trimBox(ctx, W, H) {
  const d = ctx.getImageData(0, 0, W, H).data;
  let minX = W, minY = H, maxX = 0, maxY = 0;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (d[(y * W + x) * 4 + 3] > 10) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
  }
  if (maxX <= minX || maxY <= minY) return { x: 0, y: 0, w: W, h: H };
  const pad = 6;
  return { x: Math.max(0, minX - pad), y: Math.max(0, minY - pad), w: Math.min(W, maxX - minX + pad * 2), h: Math.min(H, maxY - minY + pad * 2) };
}

const imgAspect = (url) => new Promise((res) => {
  const im = new Image();
  im.onload = () => res(im.width / Math.max(1, im.height));
  im.onerror = () => res(3);
  im.src = url;
});

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
let fieldSeq = 1;

export default function SignatureWorkbench({
  pages = [], docUrl = null, askName = true, defaultName = "",
  submitLabel = "DONE", busy = false, onSubmit, onClose = null, title = "",
  placeholders = [], allowSend = false, onSend = null,
  desktopMode = false, allowEdits = true, lockSigners = false,
  initialSigners = null, initialMessage = "", initialOrder = null,
  initialAutoRemind = null, initialRemindDays = null, startPanel = null,
}) {
  const [name, setName] = useState(defaultName);
  const [tool, setTool] = useState("sig");
  const [fields, setFields] = useState([]);
  const [selId, setSelId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [assets, setAssets] = useState({}); // {sig:{url,ar}, initials:{url,ar}}
  const [creator, setCreator] = useState(null); // {kind, spot?, fillId?}
  const [panel, setPanel] = useState(startPanel || "sign"); // sign | send
  const [sendSigners, setSendSigners] = useState(
    initialSigners?.length ? initialSigners.map((s) => ({ name: s.name || "", email: s.email || "" })) : [{ name: "", email: "" }]
  );
  const [sendMessage, setSendMessage] = useState(initialMessage || "");
  const [sendOrder, setSendOrder] = useState(initialOrder || "parallel");
  const [autoRemind, setAutoRemind] = useState(initialAutoRemind ?? false);
  const [remindDays, setRemindDays] = useState(initialRemindDays ?? 3);
  const [activeSigner, setActiveSigner] = useState(0);
  const [penDraft, setPenDraft] = useState(null);
  const [eraseDraft, setEraseDraft] = useState(null); // rectangle eraser preview
  const [wz, setWz] = useState(() => (desktopMode ? nextZ() : 100));
  const [penWidth, setPenWidth] = useState(3);
  const [eraserWidth, setEraserWidth] = useState(16);
  const [isFull, setIsFull] = useState(false);
  const [, setTick] = useState(0);
  const pageRefs = useRef({});
  const eraseRef = useRef(null);
  const initRef = useRef(false);
  const dragMovedRef = useRef(false); // swallow the stray click fired after a drag/resize
  const prepare = panel === "send";
  const lockedSign = !prepare && placeholders.length > 0; // signer only fills prepared boxes

  const moveSigner = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sendSigners.length) return;
    setSendSigners((ss) => {
      const a = [...ss];
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });
    setFields((fs) => fs.map((f) => (f.signer === i ? { ...f, signer: j } : f.signer === j ? { ...f, signer: i } : f)));
    setActiveSigner((cur) => (cur === i ? j : cur === j ? i : cur));
  };

  const switchPanel = (p) => {
    if (p === panel) return;
    if (fields.length && !window.confirm("Switching clears the boxes you placed — continue?")) return;
    setFields([]);
    setSelId(null);
    setPanel(p);
  };

  useEffect(() => {
    if (placeholders.length && !initRef.current) {
      initRef.current = true;
      setFields(placeholders.map((p) => ({ id: `f${fieldSeq++}`, placeholder: !prepare, filled: false, ...p })));
    }
  }, [placeholders, prepare]);

  useEffect(() => {
    if (!desktopMode) return;
    window.dispatchEvent(new CustomEvent("desk-wb", { detail: { open: true, title } }));
    const focus = () => setWz(nextZ());
    window.addEventListener("desk-wb-focus", focus);
    return () => {
      window.removeEventListener("desk-wb-focus", focus);
      window.dispatchEvent(new CustomEvent("desk-wb", { detail: { open: false } }));
    };
  }, [desktopMode, title]);

  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setTick((x) => x + 1), 60);
    return () => clearTimeout(t);
  }, [zoom, pages.length]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const addField = (f) => {
    const id = `f${fieldSeq++}`;
    setFields((fs) => [...fs, { id, ...f }]);
    setSelId(id);
    return id;
  };
  const patchField = (id, patch) => setFields((fs) => fs.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const removeField = (id) => { setFields((fs) => fs.filter((f) => f.id !== id)); setSelId(null); };

  const placeImageField = (kind, asset, page, relX, relY, pageAR) => {
    const w = kind === "sig" ? 0.22 : 0.07;
    const h = (w / asset.ar) * pageAR;
    addField({
      page, kind: kind === "sig" ? "signature" : "initials",
      x: clamp(relX - w / 2, 0, 1 - w), y: clamp(relY - h / 2, 0, 1 - h),
      w, h, data_url: asset.url,
    });
  };

  const onPageClick = (e, page) => {
    if (dragMovedRef.current) return;
    if (tool === "eraser" || tool === "pen") return;
    if (lockedSign) return;
    const r = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - r.left) / r.width;
    const relY = (e.clientY - r.top) / r.height;
    const pageAR = r.width / r.height;

    if (prepare) {
      const specs = {
        sig: { kind: "signature", w: 0.22, h: 0.055, required: true },
        initials: { kind: "initials", w: 0.07, h: 0.04, required: true },
        text: { kind: "text", w: 0.2, h: 0.024, value: "", required: true },
        date: { kind: "date", w: 0.12, h: 0.024, value: "", required: true },
        check: { kind: "check", w: 0.03, h: 0.03 * pageAR, required: true },
      };
      const s = specs[tool];
      if (!s) return;
      addField({
        page, ...s,
        x: clamp(relX - s.w / 2, 0, 1 - s.w), y: clamp(relY - s.h / 2, 0, 1 - s.h),
        signer: activeSigner, placeholder: true,
      });
      return;
    }

    if (tool === "sig" || tool === "initials") {
      const asset = assets[tool];
      if (!asset) { setCreator({ kind: tool, spot: { page, relX, relY, pageAR } }); return; }
      placeImageField(tool, asset, page, relX, relY, pageAR);
    } else if (tool === "text" || tool === "date") {
      const h = 0.024;
      addField({
        page, kind: tool, x: clamp(relX, 0, 0.92), y: clamp(relY - h / 2, 0, 1 - h), w: 0.2, h,
        value: tool === "date" ? new Date().toLocaleDateString() : "",
      });
    } else if (tool === "check") {
      const w = 0.03, h = w * pageAR;
      addField({ page, kind: "check", x: clamp(relX - w / 2, 0, 1 - w), y: clamp(relY - h / 2, 0, 1 - h), w, h });
    }
  };

  const onPagePointerDown = (e, page) => {
    if (lockedSign) return;
    if (tool === "eraserbox" && allowEdits) {
      // old-style rectangle white-out
      e.preventDefault();
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
      const r = e.currentTarget.getBoundingClientRect();
      const x0 = (e.clientX - r.left) / r.width;
      const y0 = (e.clientY - r.top) / r.height;
      eraseRef.current = null;
      const bmove = (ev) => {
        ev.preventDefault();
        const x1 = clamp((ev.clientX - r.left) / r.width, 0, 1);
        const y1 = clamp((ev.clientY - r.top) / r.height, 0, 1);
        const d = { page, x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
        eraseRef.current = d;
        setEraseDraft(d);
      };
      const bup = () => {
        window.removeEventListener("pointermove", bmove);
        window.removeEventListener("pointerup", bup);
        const d = eraseRef.current;
        eraseRef.current = null;
        setEraseDraft(null);
        if (d && (d.w > 0.004 || d.h > 0.004)) {
          // thin drags become a white band of the chosen eraser thickness
          const minH = eraserWidth / r.height, minW = eraserWidth / r.width;
          if (d.h < minH) { d.y = Math.max(0, d.y - (minH - d.h) / 2); d.h = minH; }
          if (d.w < minW) { d.x = Math.max(0, d.x - (minW - d.w) / 2); d.w = minW; }
          addField({ kind: "erase", ...d });
        }
      };
      window.addEventListener("pointermove", bmove, { passive: false });
      window.addEventListener("pointerup", bup);
      return;
    }
    if ((tool !== "pen" && tool !== "eraser") || !allowEdits) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    const eraser = tool === "eraser";
    const color = eraser ? "white" : undefined;
    const r = e.currentTarget.getBoundingClientRect();
    const pts = [[clamp((e.clientX - r.left) / r.width, 0, 1), clamp((e.clientY - r.top) / r.height, 0, 1)]];
    const strokeRel = (eraser ? eraserWidth : penWidth) / r.height; // fraction of page height
    const move = (ev) => {
      ev.preventDefault();
      pts.push([clamp((ev.clientX - r.left) / r.width, 0, 1), clamp((ev.clientY - r.top) / r.height, 0, 1)]);
      setPenDraft({ page, points: [...pts], stroke: strokeRel, color });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setPenDraft(null);
      if (pts.length > 1) {
        const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
        addField({
          kind: "pen", page, points: pts, stroke: strokeRel, color,
          x: Math.min(...xs), y: Math.min(...ys),
          w: Math.max(0.01, Math.max(...xs) - Math.min(...xs)),
          h: Math.max(0.01, Math.max(...ys) - Math.min(...ys)),
        });
      }
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
  };

  const fillPlaceholder = (f) => {
    if (f.kind === "signature" || f.kind === "initials") {
      const key = f.kind === "signature" ? "sig" : "initials";
      const asset = assets[key];
      if (!asset) { setCreator({ kind: key, fillId: f.id }); return; }
      patchField(f.id, { data_url: asset.url, filled: true });
    } else if (f.kind === "date") {
      if (!f.filled) patchField(f.id, { value: new Date().toLocaleDateString(), filled: true });
    } else if (f.kind === "check") {
      patchField(f.id, { filled: !f.filled });
    }
  };

  const startDrag = (e, f) => {
    if (tool === "eraser" || tool === "eraserbox") {
      e.stopPropagation();
      if (!(!prepare && f.placeholder)) removeField(f.id);
      return;
    }
    e.stopPropagation();
    setSelId(f.id);
    if (!prepare && f.placeholder) { fillPlaceholder(f); return; }
    const pageEl = pageRefs.current[f.page];
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ox = f.x, oy = f.y;
    const move = (ev) => {
      ev.preventDefault();
      dragMovedRef.current = true;
      patchField(f.id, {
        x: clamp(ox + (ev.clientX - sx) / rect.width, 0, 1 - (f.w || 0.02)),
        y: clamp(oy + (ev.clientY - sy) / rect.height, 0, 1 - (f.h || 0.02)),
      });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setTimeout(() => { dragMovedRef.current = false; }, 50);
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
  };

  const startResize = (e, f) => {
    e.stopPropagation();
    setSelId(f.id);
    const pageEl = pageRefs.current[f.page];
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, ow = f.w, oh = f.h;
    const keepAR = f.kind === "signature" || f.kind === "initials" || f.kind === "check";
    const move = (ev) => {
      ev.preventDefault();
      dragMovedRef.current = true;
      if (keepAR) {
        let nw = clamp(ow + (ev.clientX - sx) / rect.width, 0.015, 1 - f.x);
        let nh = (nw / ow) * oh;
        if (nh > 1 - f.y) { nh = 1 - f.y; nw = (nh / oh) * ow; }
        patchField(f.id, { w: nw, h: nh });
      } else if (f.kind === "text" || f.kind === "date") {
        patchField(f.id, { h: clamp(oh + (ev.clientY - sy) / rect.height, 0.01, 0.12) });
      } else {
        patchField(f.id, {
          w: clamp(ow + (ev.clientX - sx) / rect.width, 0.01, 1 - f.x),
          h: clamp(oh + (ev.clientY - sy) / rect.height, 0.005, 1 - f.y),
        });
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setTimeout(() => { dragMovedRef.current = false; }, 50);
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
  };

  const onCreatorSave = async (kindKey, url) => {
    const ar = await imgAspect(url);
    const asset = { url, ar };
    setAssets((a) => ({ ...a, [kindKey]: asset }));
    const c = creator;
    setCreator(null);
    if (c?.fillId) patchField(c.fillId, { data_url: url, filled: true });
    else if (c?.spot) placeImageField(kindKey, asset, c.spot.page, c.spot.relX, c.spot.relY, c.spot.pageAR);
  };

  const submit = () => {
    if (prepare) {
      if (!fields.length) return alert("Click the document to place at least one box for your signers.");
      const bad = sendSigners.some((s) => !s.name.trim() && !s.email.trim());
      if (bad) return alert("Give every signer a name or email (or remove empty rows).");
      const raw = fields.map(({ id, placeholder, filled, ...f }) => f);
      onSend?.({
        signers: sendSigners,
        message: sendMessage.trim(),
        signing_order: sendOrder,
        auto_remind: autoRemind,
        remind_days: remindDays,
        fields: raw.filter((f) => f.kind !== "erase" && f.kind !== "pen"),
        edits: raw.filter((f) => f.kind === "erase" || f.kind === "pen"),
      });
      return;
    }
    if (askName && !name.trim()) return alert("Please type your full name at the top.");
    const missing = fields.filter((f) => f.placeholder && f.required !== false && !f.filled && (
      f.kind === "signature" || f.kind === "initials" || f.kind === "check" ||
      (f.kind === "text" && !(f.value || "").trim())
    ));
    if (missing.length) return alert(`Please complete the ${missing.length} required field${missing.length > 1 ? "s" : ""} highlighted on the document.`);
    if (!fields.length) return alert("Click on the document to place your signature first.");
    const out = [];
    for (const f of fields) {
      const { id, placeholder, filled, signer, required, ...rest } = f;
      if (placeholder) {
        if ((f.kind === "signature" || f.kind === "initials") && !filled) continue;
        if (f.kind === "check" && !filled) continue;
        if (f.kind === "date" && !filled) rest.value = new Date().toLocaleDateString();
        if (f.kind === "text" && !(rest.value || "").trim()) continue;
      }
      out.push(rest);
    }
    const hasSig = out.some((f) => f.kind === "signature" || f.kind === "initials");
    if (!hasSig && !lockedSign && !window.confirm("You haven't placed a signature — submit anyway?")) return;
    onSubmit({ signer_name: name.trim(), fields: out });
  };

  const zoomBy = (dir) => {
    const i = ZOOMS.indexOf(zoom);
    setZoom(ZOOMS[clamp(i + dir, 0, ZOOMS.length - 1)]);
  };

  const todoFields = !prepare
    ? fields.filter((f) => f.placeholder && !f.filled && f.kind !== "text")
    : [];
  const goNextField = () => {
    const f = todoFields[0];
    if (!f) return;
    setSelId(f.id);
    document.getElementById(`sign-fieldbox-${f.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const noPages = !pages || pages.length === 0;

  return createPortal(
    <div
      className={`${desktopMode ? "absolute" : "fixed"} inset-x-0 top-0 bg-[#EAEAEA] flex flex-col`}
      style={{ bottom: desktopMode ? 48 : 0, zIndex: desktopMode ? wz : 100 }}
      onPointerDownCapture={desktopMode ? () => setWz((z) => (z < deskZ.v ? nextZ() : z)) : undefined}
      onPointerDown={desktopMode ? (e) => e.stopPropagation() : undefined}
      data-testid="signature-workbench"
    >
      {/* header */}
      <div className="bg-white/85 backdrop-blur-xl border-b border-zinc-200/60 shadow-sm text-zinc-950 flex items-center gap-3 px-4 py-2.5 flex-wrap">
        <span className="font-black text-sm tracking-tight truncate max-w-[40vw]" data-testid="sign-doc-name">{title || "Sign document"}</span>
        <span className="hidden sm:block text-xs text-zinc-500">
          {prepare ? "Place the fields each signer must fill out." : "Click on the document to fill it out & sign."}
        </span>
        <span className="flex-1" />
        {!prepare && askName && (
          <input
            className="rounded-full border border-zinc-200 bg-zinc-50 px-3.5 py-1.5 text-xs text-zinc-900 w-44 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 transition-colors"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name *"
            data-testid="sign-name-input"
          />
        )}
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white font-bold text-sm px-6 py-2 rounded-full shadow-[0_4px_14px_rgba(16,185,129,0.35)] transition-all hover:-translate-y-0.5"
          data-testid="sign-submit"
        >
          {busy ? "Working…" : prepare ? "SEND FOR SIGNING" : submitLabel}
        </button>
        {onClose && (
          <button type="button" onClick={onClose} className="p-2 rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors" data-testid="sign-close">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* mobile: panel tabs + send controls + tool strip */}
      {!noPages && (
        <div className="md:hidden bg-white/95 backdrop-blur-md border-b border-zinc-200">
          {allowSend && <PanelTabs panel={panel} switchPanel={switchPanel} prefix="sign-mtab-" />}
          {prepare && (
            <SendPanel
              signers={sendSigners} setSigners={setSendSigners}
              activeSigner={activeSigner} setActiveSigner={setActiveSigner}
              message={sendMessage} setMessage={setSendMessage}
              order={sendOrder} setOrder={setSendOrder} moveSigner={moveSigner} lockSigners={lockSigners}
              autoRemind={autoRemind} setAutoRemind={setAutoRemind} remindDays={remindDays} setRemindDays={setRemindDays}
              prefix="sign-msend-"
            />
          )}
          {lockedSign ? (
            <p className="px-3 py-2 text-xs font-semibold text-slate-600" data-testid="sign-locked-hint-m">
              {todoFields.length ? `Tap the ${todoFields.length} highlighted box${todoFields.length > 1 ? "es" : ""} to complete the document.` : "All boxes complete — press DONE."}
            </p>
          ) : (
            <div className="flex overflow-x-auto">
              <Tools tool={tool} setTool={setTool} horizontal prefix="sign-mtool-" prepare={prepare} allowEdits={allowEdits} />
            </div>
          )}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {!noPages && (
          <div className="hidden md:flex flex-col w-64 bg-white border-r border-zinc-200 overflow-y-auto">
            {allowSend && <PanelTabs panel={panel} switchPanel={switchPanel} />}
            {prepare && (
              <SendPanel
                signers={sendSigners} setSigners={setSendSigners}
                activeSigner={activeSigner} setActiveSigner={setActiveSigner}
                message={sendMessage} setMessage={setSendMessage}
                order={sendOrder} setOrder={setSendOrder} moveSigner={moveSigner} lockSigners={lockSigners}
              autoRemind={autoRemind} setAutoRemind={setAutoRemind} remindDays={remindDays} setRemindDays={setRemindDays}
              />
            )}
            {lockedSign ? (
              <div className="p-4 space-y-2" data-testid="sign-locked-panel">
                <p className="text-[11px] font-bold tracking-wide text-slate-400">COMPLETE YOUR FIELDS</p>
                <p className="text-xs text-slate-600">
                  {todoFields.length
                    ? `${todoFields.length} box${todoFields.length > 1 ? "es" : ""} left — tap the highlighted spots on the document. Boxes marked (optional) can be skipped.`
                    : "All boxes complete — press DONE at the top."}
                </p>
              </div>
            ) : (
              <>
                <p className="px-4 pt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-2">
                  {prepare ? "Place boxes for the selected signer" : "Sign & edit"}
                </p>
                <Tools tool={tool} setTool={setTool} prepare={prepare} allowEdits={allowEdits} />
              </>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto min-w-0" onClick={() => setSelId(null)}>
          {noPages ? (
            <FallbackPanel docUrl={docUrl} asset={assets.sig} onCreate={() => setCreator({ kind: "sig" })} fields={fields} addField={addField} removeField={removeField} />
          ) : (
            <>
              <div className="sticky top-2 z-10 mx-auto w-fit max-w-[97%] flex items-center justify-center gap-1.5 px-2.5 py-1.5 mb-1 rounded-full bg-white/95 backdrop-blur-xl border border-zinc-200/60 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.12)] text-xs flex-wrap">
                <button type="button" onClick={() => zoomBy(-1)} className="p-2 rounded-full hover:bg-zinc-100 text-zinc-700 transition-colors" data-testid="sign-zoom-out"><ZoomOut className="w-4 h-4" /></button>
                <span className="font-bold w-11 text-center text-zinc-900" data-testid="sign-zoom-label">{Math.round(zoom * 100)}%</span>
                <button type="button" onClick={() => zoomBy(1)} className="p-2 rounded-full hover:bg-zinc-100 text-zinc-700 transition-colors" data-testid="sign-zoom-in"><ZoomIn className="w-4 h-4" /></button>
                <span className="w-px h-5 bg-zinc-200 mx-0.5 hidden sm:block" />
                <button type="button" onClick={toggleFullscreen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-zinc-100 text-zinc-700 font-semibold transition-colors" data-testid="sign-fullscreen">
                  {isFull ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                  {isFull ? "Exit full screen" : "Full screen"}
                </button>
                {tool === "eraser" && (
                  <span className="inline-flex items-center gap-2 font-bold text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-3.5 py-1" data-testid="sign-eraser-hint">
                    Eraser: draw to white out · tap an item to delete — thickness
                    <input
                      type="range" min={6} max={60} value={eraserWidth}
                      onChange={(e) => setEraserWidth(parseInt(e.target.value))}
                      className="w-24 accent-rose-600"
                      data-testid="sign-eraser-width"
                    />
                    <span className="w-8 text-center">{eraserWidth}px</span>
                  </span>
                )}
                {tool === "eraserbox" && (
                  <span className="inline-flex items-center gap-2 font-bold text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-3.5 py-1" data-testid="sign-eraserbox-hint">
                    Eraser box: drag a rectangle to white out · tap an item to delete — thickness
                    <input
                      type="range" min={6} max={60} value={eraserWidth}
                      onChange={(e) => setEraserWidth(parseInt(e.target.value))}
                      className="w-24 accent-rose-600"
                      data-testid="sign-eraserbox-width"
                    />
                    <span className="w-8 text-center">{eraserWidth}px</span>
                  </span>
                )}
                {tool === "pen" && (
                  <span className="inline-flex items-center gap-2 font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-3.5 py-1" data-testid="sign-pen-hint">
                    Pen — thickness
                    <input
                      type="range" min={1} max={14} value={penWidth}
                      onChange={(e) => setPenWidth(parseInt(e.target.value))}
                      className="w-24 accent-sky-600"
                      data-testid="sign-pen-width"
                    />
                    <span className="w-6 text-center">{penWidth}px</span>
                  </span>
                )}
              </div>
              <div className="px-3 pb-20 pt-3 sm:px-8">
                <div className="mx-auto space-y-4" style={{ width: `${zoom * 100}%` }}>
                  {pages.map((p, i) => (
                    <div
                      key={i}
                      ref={(el) => { pageRefs.current[i] = el; }}
                      className={`relative bg-white rounded-sm border border-zinc-200 shadow-[0_0_50px_-12px_rgba(0,0,0,0.25)] ${tool === "eraser" || tool === "eraserbox" || tool === "pen" ? "cursor-cell touch-none" : "cursor-crosshair"}`}
                      onClick={(e) => { e.stopPropagation(); setSelId(null); onPageClick(e, i); }}
                      onPointerDown={(e) => onPagePointerDown(e, i)}
                      data-testid={`sign-page-${i}`}
                    >
                      <img src={p} alt={`page ${i + 1}`} className="w-full block pointer-events-none select-none" draggable={false} onLoad={() => setTick((x) => x + 1)} />
                      {eraseDraft && eraseDraft.page === i && (
                        <div
                          className="absolute bg-white/80 outline-dashed outline-2 outline-red-400 pointer-events-none"
                          style={{ left: `${eraseDraft.x * 100}%`, top: `${eraseDraft.y * 100}%`, width: `${eraseDraft.w * 100}%`, height: `${eraseDraft.h * 100}%` }}
                          data-testid="sign-erase-draft"
                        />
                      )}
                      {fields.filter((f) => f.page === i && f.kind === "pen").concat(penDraft && penDraft.page === i ? [{ ...penDraft, id: "__draft", kind: "pen" }] : []).map((f) => {
                        const pH = pageRefs.current[i]?.clientHeight || 900;
                        const white = f.color === "white";
                        return (
                          <React.Fragment key={f.id}>
                            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 1000" preserveAspectRatio="none">
                              <polyline
                                points={(f.points || []).map(([px, py]) => `${px * 1000},${py * 1000}`).join(" ")}
                                fill="none" stroke={white ? "#ffffff" : "#1e293b"}
                                strokeWidth={Math.max(1, (f.stroke || 0.004) * pH)}
                                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                              />
                              {white && f.id === "__draft" && (
                                <polyline
                                  points={(f.points || []).map(([px, py]) => `${px * 1000},${py * 1000}`).join(" ")}
                                  fill="none" stroke="#f87171" strokeWidth={1.5} strokeDasharray="5 4"
                                  strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                                />
                              )}
                            </svg>
                            {(tool === "eraser" || tool === "eraserbox") && f.id !== "__draft" && (
                              <div
                                className="absolute cursor-pointer outline-dashed outline-1 outline-red-300 hover:bg-red-100/30"
                                style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}
                                onPointerDown={(ev) => { ev.stopPropagation(); removeField(f.id); }}
                                title="Tap to delete this pen stroke"
                                data-testid={`sign-pen-${f.id}`}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                      {fields.filter((f) => f.page === i && f.kind !== "pen").map((f) => (
                        <FieldBox
                          key={f.id}
                          f={f}
                          selected={selId === f.id}
                          eraser={tool === "eraser" || tool === "eraserbox"}
                          pageEl={pageRefs.current[i]}
                          signerName={prepare ? (sendSigners[f.signer]?.name || `Signer ${(f.signer ?? 0) + 1}`) : ""}
                          mode={prepare ? "prepare" : "sign"}
                          pal={SIGNER_PALETTE[(f.signer ?? 0) % 5]}
                          onPointerDown={(e) => startDrag(e, f)}
                          onResizeStart={(e) => startResize(e, f)}
                          onRemove={() => removeField(f.id)}
                          onToggleRequired={() => patchField(f.id, { required: f.required === false })}
                          onChange={(v) => patchField(f.id, { value: v, filled: true })}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {todoFields.length > 0 && (
        <button
          type="button"
          onClick={goNextField}
          className="fixed bottom-14 right-4 z-[110] bg-amber-500 hover:bg-amber-400 text-amber-950 font-black tracking-tight text-sm px-5 py-3 rounded-full shadow-[0_8px_20px_rgba(245,158,11,0.4)] animate-bounce hover:animate-none transition-colors"
          data-testid="sign-next-field"
        >
          Next field → ({todoFields.length} left)
        </button>
      )}

      <div className="bg-zinc-900 text-center text-[10px] text-zinc-400 font-medium tracking-wide py-1.5">
        {prepare
          ? "Fields you place here will be required from each signer."
          : "By signing you agree this electronic signature is as valid as a handwritten one."}
      </div>

      {creator && (
        <SignatureCreator
          kind={creator.kind}
          defaultText={creator.kind === "initials"
            ? (name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase() || "")
            : name.trim()}
          onCancel={() => setCreator(null)}
          onSave={(url) => onCreatorSave(creator.kind, url)}
        />
      )}
    </div>,
    (desktopMode && document.getElementById("mgmt-desktop-root")) || document.body
  );
}

function PanelTabs({ panel, switchPanel, prefix = "sign-tab-panel-" }) {
  return (
    <div className="flex border-b border-zinc-200">
      <button
        type="button"
        onClick={() => switchPanel("sign")}
        className={`flex-1 text-xs font-bold py-3 transition-colors ${panel === "sign" ? "text-zinc-950 border-b-2 border-zinc-950 bg-zinc-50" : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50"}`}
        data-testid={`${prefix}sign`}
      >
        Fill & Sign
      </button>
      <button
        type="button"
        onClick={() => switchPanel("send")}
        className={`flex-1 text-xs font-bold py-3 transition-colors ${panel === "send" ? "text-zinc-950 border-b-2 border-zinc-950 bg-zinc-50" : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50"}`}
        data-testid={`${prefix}send`}
      >
        Send to Sign
      </button>
    </div>
  );
}

function SendPanel({ signers, setSigners, activeSigner, setActiveSigner, message, setMessage, order, setOrder, moveSigner, autoRemind, setAutoRemind, remindDays, setRemindDays, prefix = "sign-send-", lockSigners = false }) {
  const patch = (i, k, v) => setSigners((ss) => ss.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="border-b border-zinc-200 px-3 py-3 space-y-2" data-testid={`${prefix}panel`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">Signers {order === "sequential" ? "(in this order)" : ""}</p>
      {signers.map((s, i) => (
        <div
          key={i}
          onClick={() => setActiveSigner(i)}
          className={`rounded-xl border-2 p-2 space-y-1 cursor-pointer transition-colors ${activeSigner === i ? SIGNER_PALETTE[i % 5].border + " " + SIGNER_PALETTE[i % 5].bg : "border-zinc-200 hover:border-zinc-300"}`}
          data-testid={`${prefix}signer-${i}`}
        >
          <div className="flex items-center gap-1.5">
            <span className={`w-4 h-4 rounded-full shrink-0 text-white text-[9px] font-black flex items-center justify-center ${SIGNER_PALETTE[i % 5].chip}`}>{i + 1}</span>
            <input
              className="flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-1 text-xs bg-white"
              placeholder={`Signer ${i + 1} name`}
              value={s.name}
              onChange={(e) => patch(i, "name", e.target.value)}
              data-testid={`${prefix}name-${i}`}
            />
            <div className="flex flex-col shrink-0" style={{ display: lockSigners ? "none" : undefined }}>
              <button type="button" className="p-0 text-slate-400 hover:text-slate-700 disabled:opacity-20 leading-none" disabled={i === 0}
                onClick={(e) => { e.stopPropagation(); moveSigner(i, -1); }} title="Move up" data-testid={`${prefix}up-${i}`}>
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" className="p-0 text-slate-400 hover:text-slate-700 disabled:opacity-20 leading-none" disabled={i === signers.length - 1}
                onClick={(e) => { e.stopPropagation(); moveSigner(i, 1); }} title="Move down" data-testid={`${prefix}down-${i}`}>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
            {signers.length > 1 && !lockSigners && (
              <button
                type="button"
                className="p-0.5 rounded text-red-500 hover:bg-red-50 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setSigners((ss) => ss.filter((_, j) => j !== i));
                  setActiveSigner(0);
                }}
                data-testid={`${prefix}remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input
            className="w-full border border-slate-300 rounded px-1.5 py-1 text-xs bg-white"
            placeholder="Email (optional)"
            value={s.email}
            onChange={(e) => patch(i, "email", e.target.value)}
            data-testid={`${prefix}email-${i}`}
          />
        </div>
      ))}
      {!lockSigners && (
        <button
          type="button"
          className="text-xs font-bold text-emerald-700 hover:text-emerald-600"
          onClick={() => { setSigners((ss) => [...ss, { name: "", email: "" }]); setActiveSigner(signers.length); }}
          data-testid={`${prefix}add`}
        >
          + Add signer
        </button>
      )}
      <div className="space-y-1 pt-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">Signing order</p>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="radio" checked={order === "parallel"} onChange={() => setOrder("parallel")} data-testid={`${prefix}order-parallel`} />
          Everyone at once — anyone can sign anytime
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="radio" checked={order === "sequential"} onChange={() => setOrder("sequential")} data-testid={`${prefix}order-sequential`} />
          In order — each signer is emailed after the previous one finishes
        </label>
      </div>
      <div className="space-y-1 pt-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">Auto reminders</p>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={autoRemind} onChange={(e) => setAutoRemind(e.target.checked)} data-testid={`${prefix}auto-remind`} />
          Remind signers automatically every
          <input
            type="number" min={1} max={60}
            className="w-12 border border-slate-300 rounded px-1 py-0.5 text-xs"
            value={remindDays}
            onChange={(e) => setRemindDays(Math.max(1, Math.min(60, parseInt(e.target.value) || 1)))}
            data-testid={`${prefix}remind-days`}
          />
          day(s)
        </label>
      </div>
      <textarea
        className="w-full border border-zinc-200 bg-zinc-50 rounded-lg px-2.5 py-2 text-xs focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 outline-none transition-colors"
        rows={2}
        placeholder="Message to signers (optional)"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        data-testid={`${prefix}message`}
      />
    </div>
  );
}

const TOOL_DEFS = [
  { id: "sig", label: "My Signature", plabel: "Signature", icon: PenLine },
  { id: "initials", label: "My Initials", plabel: "Initials", icon: null },
  { id: "text", label: "Text", plabel: "Text", icon: Type },
  { id: "date", label: "Date Signed", plabel: "Date Signed", icon: CalendarDays },
  { id: "check", label: "Checkmark", plabel: "Checkmark", icon: CheckSquare },
  { id: "pen", label: "Pen", plabel: "Pen", icon: Brush, editTool: true },
  { id: "eraser", label: "Eraser", plabel: "Eraser", icon: Eraser, editTool: true },
  { id: "eraserbox", label: "Eraser box", plabel: "Eraser box", icon: Square, editTool: true },
];

function Tools({ tool, setTool, horizontal = false, prefix = "sign-tool-", prepare = false, allowEdits = true }) {
  return TOOL_DEFS.filter((t) => allowEdits || !t.editTool).map((t) => (
    <button
      key={t.id}
      type="button"
      onClick={() => setTool(t.id)}
      className={`flex items-center gap-2.5 text-sm font-semibold whitespace-nowrap transition-all ${horizontal ? "px-3.5 py-2.5 border-b-2" : "px-4 py-2.5 mx-2 my-0.5 rounded-lg text-left border-l-4"} ${tool === t.id ? "bg-zinc-100 text-zinc-950 font-bold border-zinc-950" : "text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 border-transparent"}`}
      data-testid={`${prefix}${t.id}`}
    >
      {t.icon
        ? <t.icon className="w-4 h-4" />
        : <span className="text-[11px] font-black border border-current rounded px-0.5 leading-tight">IN</span>}
      {prepare ? t.plabel : t.label}
    </button>
  ));
}

function FieldBox({ f, mode, selected, eraser, pageEl, signerName, pal, onPointerDown, onResizeStart, onRemove, onToggleRequired, onChange }) {
  const isTextKind = f.kind === "text" || f.kind === "date";
  const pageH = pageEl?.clientHeight || 900;
  const fontPx = Math.max(9, f.h * pageH * 0.72);
  const prepare = mode === "prepare";
  const signPh = mode === "sign" && f.placeholder;
  const showInput = isTextKind && (prepare ? f.kind === "text" : (!f.placeholder || f.kind === "text" || f.filled));
  const protectedPh = signPh; // signers can't delete required fields
  const resizable = !eraser && !(signPh && !f.filled);
  const optional = f.required === false;
  const optTag = signPh && optional && !f.filled ? " (optional)" : "";

  let content = null;
  if (f.kind === "erase") {
    content = <div className="w-full h-full bg-white outline-dashed outline-1 outline-red-200 pointer-events-none" />;
  } else if ((f.kind === "signature" || f.kind === "initials") && f.data_url && (!f.placeholder || f.filled)) {
    content = <img src={f.data_url} alt={f.kind} className="w-full h-full object-contain pointer-events-none select-none" draggable={false} />;
  } else if (f.kind === "signature" || f.kind === "initials") {
    content = (
      <div className={`w-full h-full border-2 border-dashed rounded flex items-center justify-center gap-1 text-[10px] font-bold pointer-events-none ${pal.border} ${pal.bg} ${pal.text}`}>
        ✍ {f.kind === "signature" ? (signPh ? "Tap to sign" : "Sign here") : "Initials"}
        {prepare && signerName ? ` — ${signerName}` : ""}{prepare && !optional ? " ✱" : ""}{optTag}
      </div>
    );
  } else if (f.kind === "check") {
    content = (f.placeholder && !f.filled)
      ? <div className={`w-full h-full border-2 border-dashed rounded pointer-events-none ${pal.border} ${pal.bg}`} />
      : <Check className="w-full h-full text-slate-800 pointer-events-none" strokeWidth={3} />;
  } else if (f.kind === "date" && f.placeholder && !f.filled && !prepare) {
    content = (
      <div className={`border-2 border-dashed rounded px-1.5 py-0.5 font-bold pointer-events-none whitespace-nowrap ${pal.border} ${pal.bg} ${pal.text}`} style={{ fontSize: fontPx }}>
        📅 Tap for date{optTag}
      </div>
    );
  } else if (f.kind === "date" && prepare) {
    content = (
      <div className={`border-2 border-dashed rounded px-1.5 py-0.5 font-bold pointer-events-none whitespace-nowrap ${pal.border} ${pal.bg} ${pal.text}`} style={{ fontSize: fontPx }}>
        📅 Date (auto)
      </div>
    );
  }

  return (
    <div
      id={`sign-fieldbox-${f.id}`}
      className={`absolute touch-none group ${eraser ? "cursor-pointer" : signPh ? "cursor-pointer" : "cursor-move"} ${selected ? "ring-2 ring-sky-500" : "hover:ring-1 hover:ring-sky-300"} ${eraser && !protectedPh ? "hover:ring-2 hover:ring-red-400" : ""}`}
      style={{
        left: `${f.x * 100}%`, top: `${f.y * 100}%`,
        ...(isTextKind ? {} : { width: `${f.w * 100}%`, height: `${f.h * 100}%` }),
      }}
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      data-testid={`sign-field-${f.kind}${f.placeholder ? "-ph" : ""}`}
    >
      {content}
      {showInput && (
        <input
          value={f.value || ""}
          onChange={(e) => onChange(e.target.value)}
          onPointerDown={(e) => { if (!eraser) e.stopPropagation(); }}
          placeholder={f.kind === "date" ? "Date" : prepare ? "Prefill text (optional)" : f.placeholder ? (f.required === false ? "Optional — type here" : "Required * — type here") : "Type here"}
          size={Math.max((f.value || "").length + 1, prepare ? 16 : 7)}
          style={{ fontSize: fontPx, lineHeight: 1.2 }}
          className={`border rounded px-1 text-slate-900 outline-none focus:border-sky-500 ${eraser ? "pointer-events-none" : ""} ${f.placeholder ? `${pal.bg} ${pal.border}` : "bg-amber-50/90 border-amber-300"}`}
          data-testid={`sign-field-input-${f.kind}`}
        />
      )}
      {!protectedPh && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className={`absolute -top-2.5 -right-2.5 bg-red-500 text-white rounded-full w-5 h-5 items-center justify-center shadow ${selected ? "flex" : "hidden group-hover:flex"}`}
          title="Remove"
          data-testid="sign-field-remove"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      {resizable && (
        <div
          onPointerDown={onResizeStart}
          onClick={(e) => e.stopPropagation()}
          className={`absolute -bottom-2 -right-2 w-4 h-4 bg-sky-600 border-2 border-white rounded-full cursor-nwse-resize shadow touch-none ${selected ? "block" : "hidden group-hover:block"}`}
          title="Drag to resize"
          data-testid="sign-field-resize"
        />
      )}
      {prepare && f.kind !== "erase" && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onToggleRequired(); }}
          className={`absolute -bottom-3 left-0 text-[9px] font-black px-1.5 py-px rounded-full shadow border whitespace-nowrap ${selected ? "block" : "hidden group-hover:block"} ${optional ? "bg-slate-100 text-slate-500 border-slate-300" : "bg-red-500 text-white border-red-500"}`}
          title="Click to toggle required / optional"
          data-testid="sign-field-required-toggle"
        >
          {optional ? "OPTIONAL" : "REQUIRED ✱"}
        </button>
      )}
    </div>
  );
}

function FallbackPanel({ docUrl, asset, onCreate, fields, addField, removeField }) {
  const placed = fields.find((f) => f.kind === "signature");
  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow p-5 mt-10 text-center space-y-4" data-testid="sign-fallback">
      <p className="text-sm text-slate-600">This file type can't be previewed — download and review it, then attach your signature.</p>
      {docUrl && (
        <a href={docUrl} download className="inline-flex items-center gap-1.5 text-sky-700 font-semibold underline text-sm">
          <Download className="w-4 h-4" /> Download the document
        </a>
      )}
      {asset ? (
        <div className="border border-slate-200 rounded p-3">
          <img src={asset.url} alt="signature" className="max-h-20 mx-auto" />
          {!placed && (
            <button type="button" className="mt-2 text-xs font-semibold text-emerald-700 underline"
              onClick={() => addField({ page: 0, kind: "signature", x: 0.6, y: 0.85, w: 0.25, h: 0.08, data_url: asset.url })}>
              Attach this signature
            </button>
          )}
          {placed && (
            <p className="text-xs text-emerald-700 font-semibold mt-2">✓ Signature attached
              <button type="button" className="ml-2 text-red-500 underline" onClick={() => removeField(placed.id)}>remove</button>
            </p>
          )}
        </div>
      ) : (
        <button type="button" onClick={onCreate} className="bg-emerald-600 text-white font-bold text-sm px-4 py-2 rounded" data-testid="sign-fallback-create">
          Create my signature
        </button>
      )}
    </div>
  );
}

export function SignatureCreator({ kind = "sig", defaultText = "", onCancel, onSave }) {
  const [tab, setTab] = useState("type");
  const [text, setText] = useState(defaultText);
  const [font, setFont] = useState(SIG_FONTS[0].id);
  const [drawn, setDrawn] = useState(false);
  const [penTool, setPenTool] = useState("pen");
  const [uploadUrl, setUploadUrl] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const uploadRef = useRef(null);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return [(t.clientX - r.left) * (c.width / r.width), (t.clientY - r.top) * (c.height / r.height)];
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const [x, y] = pos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.globalCompositeOperation = penTool === "eraser" ? "destination-out" : "source-over";
    ctx.lineWidth = penTool === "eraser" ? 24 : 2.5;
    ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    const [x, y] = pos(e);
    ctx.lineTo(x, y); ctx.stroke();
    if (penTool === "pen") setDrawn(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  };
  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fr = new FileReader();
    fr.onload = () => setUploadUrl(fr.result);
    fr.readAsDataURL(f);
    e.target.value = "";
  };

  const save = () => {
    if (tab === "draw") {
      if (!drawn) return alert("Draw your signature first (or switch to Type).");
      const c = canvasRef.current;
      const box = trimBox(c.getContext("2d"), c.width, c.height);
      const t = document.createElement("canvas");
      t.width = box.w; t.height = box.h;
      t.getContext("2d").drawImage(c, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
      return onSave(t.toDataURL("image/png"));
    }
    if (tab === "upload") {
      if (!uploadUrl) return alert("Upload a signature image first.");
      return onSave(uploadUrl);
    }
    const v = text.trim();
    if (!v) return alert(kind === "initials" ? "Type your initials." : "Type your name.");
    document.fonts.ready.then(() => onSave(typedSigDataUrl(v, SIG_FONTS.find((x) => x.id === font).css)));
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-3" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl border border-zinc-200 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="sign-creator">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-black tracking-tight text-base">{kind === "initials" ? "Create your initials" : "Create your signature"}</h4>
          <button type="button" onClick={onCancel} className="p-1.5 rounded-full hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-1 mb-3 border-b border-slate-200">
          <TabBtn active={tab === "type"} onClick={() => setTab("type")} icon={Type} label="Type" testid="sign-tab-type" />
          <TabBtn active={tab === "draw"} onClick={() => setTab("draw")} icon={PenLine} label="Draw" testid="sign-tab-draw" />
          <TabBtn active={tab === "upload"} onClick={() => setTab("upload")} icon={Upload} label="Upload" testid="sign-tab-upload" />
        </div>

        {tab === "type" && (
          <div data-testid="sign-type-panel">
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 mb-2 text-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={kind === "initials" ? "Your initials (e.g. JS)" : "Type your name"}
              data-testid="sign-type-input"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto">
              {SIG_FONTS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFont(f.id)}
                  className={`border-2 rounded-md px-3 py-1.5 text-left bg-white ${font === f.id ? "border-emerald-500 ring-1 ring-emerald-300" : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`sign-font-${f.id}`}
                >
                  <span className="block text-2xl leading-snug text-slate-800" style={{ fontFamily: f.css }}>
                    {text.trim() || (kind === "initials" ? "AB" : "Your Name")}
                  </span>
                  <span className="text-[10px] text-slate-400">{f.label}{font === f.id ? " · selected ✓" : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "draw" && (
          <div data-testid="sign-draw-panel">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <ToolBtn active={penTool === "pen"} onClick={() => setPenTool("pen")} icon={PenLine} label="Pen" testid="sigpad-pen" />
                <ToolBtn active={penTool === "eraser"} onClick={() => setPenTool("eraser")} icon={Eraser} label="Eraser" testid="sigpad-eraser" />
              </div>
              <button type="button" onClick={clear} className="text-xs text-slate-500 underline" data-testid="sign-clear">Clear all</button>
            </div>
            <canvas
              ref={canvasRef}
              width={560}
              height={160}
              className={`w-full border-2 border-dashed border-slate-300 rounded-md bg-white touch-none ${penTool === "eraser" ? "cursor-cell" : "cursor-crosshair"}`}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="sign-canvas"
            />
          </div>
        )}

        {tab === "upload" && (
          <div data-testid="sign-upload-panel">
            <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="sign-upload-input" />
            {uploadUrl ? (
              <div className="border-2 border-dashed border-slate-300 rounded-md bg-white p-3 flex flex-col items-center gap-2">
                <img src={uploadUrl} alt="uploaded signature" className="max-h-24 object-contain" />
                <button type="button" className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-100 font-semibold" onClick={() => uploadRef.current?.click()}>Replace</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => uploadRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-md bg-white py-8 text-sm text-slate-500 hover:border-slate-400 flex flex-col items-center gap-2"
                data-testid="sign-upload-btn"
              >
                <Upload className="w-6 h-6" />
                Upload an image of your signature
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={save}
          className="mt-4 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 rounded-full text-sm shadow-[0_4px_14px_rgba(16,185,129,0.3)] transition-colors"
          data-testid="sign-creator-save"
        >
          Use this {kind === "initials" ? "initials" : "signature"}
        </button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border-b-2 -mb-px ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function ToolBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border ${active ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 hover:bg-slate-100"}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
